// price-inquiry-steps.js - the shared 3-steg prisförfrågan form on turbo.se (2026-09-08).
// Steg 1 reg nr/turbonummer + e-post, steg 2 symtom, steg 3 namn + telefon, one POST at the end
// to the inskick Worker. Rendered by snippets/price-inquiry-steps.liquid on renovera-turbo (strip +
// exit popup), marin, lastbil, industri and the vehicle-search "ingen träff" modal.
// Spec: docs/superpowers/specs/2026-09-08-prisforfragan-3-steg-design.md (turbo.se repo).
//
// ES module. The pure helpers below are unit-tested with node (tasks/prisforfragan-3-steg/test);
// the DOM wiring at the bottom only runs in a browser. Loaded as <script type="module"> from the
// snippet - a module executes once per page no matter how many forms rendered the tag.

export const ENDPOINT = 'https://send.turbo.se/api/inskick/prisforfragan';
export const SYMPTOM_SLUGS = ['rok', 'ljud', 'effekt', 'motorlampa', 'olja', 'intercooler', 'vet_inte', 'annat'];
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const COPY = {
  progress: function (n) { return 'Steg ' + n + ' av 3'; },
  errIdentifierRegnr: 'Fyll i ditt registreringsnummer.',
  errIdentifierTurbonummer: 'Fyll i ditt turbonummer.',
  errEmail: 'Fyll i en giltig e-postadress.',
  errSymptoms: 'Kryssa i minst ett alternativ eller beskriv vad du märkt.',
  errAnnat: 'Beskriv kort vad du menar med Annat.',
  errName: 'Fyll i ditt namn.',
  errPhone: 'Kontrollera telefonnumret, eller lämna fältet tomt.',
  errTurnstile: 'Säkerhetskontrollen kunde inte slutföras. Ladda om sidan och försök igen.',
  err422: 'Kontrollera uppgifterna och försök igen.',
  errGeneric: 'Något gick fel. Försök igen om en stund.',
  success: 'Tack! Vi mejlar dig ett pris så snart vi kan.',
  sending: 'Skickar...',
  sent: 'Skickat',
  submit: 'Maila mig ett pris',
};

// ---- pure validation (mirrors the Worker rules so a valid step never 422s) ----------------

export function validateStep1(v) {
  var id = String(v.identifier || '').trim();
  var email = String(v.email || '').trim();
  if (v.identifierType === 'turbonummer') {
    if (!/^[A-Za-z0-9][A-Za-z0-9\s.\/-]{1,19}$/.test(id)) return {ok: false, field: 'identifier', message: COPY.errIdentifierTurbonummer};
  } else if (!/^[A-Za-z0-9]{2,10}$/.test(id.replace(/[\s-]/g, ''))) {
    return {ok: false, field: 'identifier', message: COPY.errIdentifierRegnr};
  }
  if (!email || !EMAIL_RE.test(email)) return {ok: false, field: 'email', message: COPY.errEmail};
  return {ok: true};
}

export function validateStep2(v) {
  var chosen = (v.symptoms || []).filter(function (s) { return SYMPTOM_SLUGS.indexOf(s) !== -1; });
  var text = String(v.text || '').trim();
  if (text.length >= 3) return {ok: true};
  if (chosen.length === 0) return {ok: false, field: 'symptoms', message: COPY.errSymptoms};
  if (chosen.length === 1 && chosen[0] === 'annat') return {ok: false, field: 'text', message: COPY.errAnnat};
  return {ok: true};
}

export function validateStep3(v) {
  var name = String(v.name || '').trim();
  var phone = String(v.phone || '').trim();
  if (name.length < 2) return {ok: false, field: 'name', message: COPY.errName};
  if (phone && !/^[+0-9][0-9 ()\/-]{4,24}$/.test(phone)) return {ok: false, field: 'phone', message: COPY.errPhone};
  return {ok: true};
}

// The POST body. Today's keys stay byte-identical (source/form_variant/traffic_source/event_id +
// the attribution spread); the four new keys are always present so the Worker sees one shape.
export function buildBody(state, ctx) {
  var body = {};
  if (state.identifierType === 'turbonummer') body.turbonummer = String(state.identifier || '').trim();
  else body.regnr = String(state.identifier || '').trim().toUpperCase().replace(/\s+/g, '');
  body.email = String(state.email || '').trim();
  body.name = String(state.name || '').trim().slice(0, 100);
  body.phone = String(state.phone || '').trim();
  body.symptoms = (state.symptoms || []).filter(function (s) { return SYMPTOM_SLUGS.indexOf(s) !== -1; });
  body.symptoms_text = String(state.text || '').trim().slice(0, 1000);
  body.turnstileToken = ctx.token;
  body.nonce = ctx.nonce;
  body.source = ctx.source;
  body.form_variant = ctx.formVariant;
  body.traffic_source = ctx.trafficSource || '';
  body.event_id = ctx.eventId;
  return Object.assign(body, ctx.attr || {});
}

// ---- DOM wiring ------------------------------------------------------------------------

export function initForm(form, win) {
  if (form.__pis) return;
  form.__pis = true;
  var doc = form.ownerDocument;
  var identifierType = form.getAttribute('data-identifier') === 'turbonummer' ? 'turbonummer' : 'regnr';
  var source = form.getAttribute('data-source') || 'renovera-turbo';
  var placement = form.getAttribute('data-placement') || 'strip';
  var formVariant = form.getAttribute('data-form-variant') || 'price_inline';
  var clarityId = form.getAttribute('data-clarity-id') || ('prisforfragan-' + placement);
  var steps = Array.prototype.slice.call(form.querySelectorAll('[data-pi-step]'));
  var progress = form.querySelector('[data-pi-progress]');
  var errEl = form.querySelector('[data-pi-error]');
  var msg = doc.getElementById(form.getAttribute('data-pi-msg') || '');
  var submitBtn = form.querySelector('[data-pi-submit]');
  var ts = form.querySelector('.cf-turnstile');
  var current = 1;
  var reportedStep = 0;

  function field(name) { return form.elements.namedItem(name); }
  function fields() {
    return {
      identifier: field(identifierType === 'turbonummer' ? 'turbonummer' : 'regnr'),
      email: field('email'),
      text: field('symptoms_text'),
      name: field('name'),
      phone: field('phone'),
    };
  }
  function state() {
    var f = fields();
    return {
      identifierType: identifierType,
      identifier: f.identifier ? f.identifier.value : '',
      email: f.email ? f.email.value : '',
      symptoms: Array.prototype.slice.call(form.querySelectorAll('input[name="symptoms"]:checked')).map(function (el) { return el.value; }),
      text: f.text ? f.text.value : '',
      name: f.name ? f.name.value : '',
      phone: f.phone ? f.phone.value : '',
    };
  }
  function showError(res) {
    var f = fields();
    Object.keys(f).forEach(function (k) { if (f[k] && f[k].classList) f[k].classList.remove('is-invalid'); });
    if (!res || res.ok) { if (errEl) { errEl.hidden = true; errEl.textContent = ''; } return; }
    var el = f[res.field];
    if (el && el.classList) { el.classList.add('is-invalid'); try { el.focus(); } catch (e) {} }
    if (errEl) { errEl.textContent = res.message; errEl.hidden = false; }
  }
  function show(n) {
    current = n;
    steps.forEach(function (s) { s.hidden = Number(s.getAttribute('data-pi-step')) !== n; });
    if (progress) progress.textContent = COPY.progress(n);
    showError(null);
  }
  function validateCurrent() {
    var s = state();
    if (current === 1) return validateStep1(s);
    if (current === 2) return validateStep2(s);
    return validateStep3(s);
  }
  // Drop-off measurement: a completed step 1 or 2, once per form instance (back-and-forward
  // through an already-reported step must not re-fire request_price_step).
  function trackStep(n) {
    if (n <= reportedStep) return;
    if (win.gtag && (win.turboMarketingAllowed ? win.turboMarketingAllowed() : false)) {
      try { win.gtag('event', 'request_price_step', {send_to: 'G-3KZXR1WSF3', step: n, placement: placement, business_line: 'renovering'}); reportedStep = n; } catch (e) {}
    }
    if (typeof win.clarity === 'function') { try { win.clarity('set', 'lead_step', String(n + 1)); } catch (e) {} }
  }
  function resetSubmit() {
    if (win.turnstile && ts) { try { win.turnstile.reset(ts); } catch (e) {} }
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = COPY.submit; }
  }
  function resetAll() {
    try { form.reset(); } catch (e) {}
    form.hidden = false;
    if (msg) msg.hidden = true;
    resetSubmit();
    reportedStep = 0;
    show(1);
  }
  form.__pisReset = resetAll;

  form.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-pi-next],[data-pi-back]') : null;
    if (!t || !form.contains(t)) return;
    e.preventDefault();
    if (t.hasAttribute('data-pi-back')) { show(Math.max(1, current - 1)); return; }
    var res = validateCurrent();
    if (!res.ok) { showError(res); return; }
    trackStep(current);
    show(current + 1);
  });
  // Enter on step 1 or 2 advances instead of submitting the whole form - but not when the
  // target is a button or a link (e.g. Tillbaka), which should get its own native click.
  form.addEventListener('keydown', function (e) {
    if (e.target && e.target.closest && e.target.closest('button, a')) return;
    if (e.key !== 'Enter' || current === 3) return;
    if (e.target && e.target.tagName === 'TEXTAREA') return;
    e.preventDefault();
    var next = steps[current - 1] && steps[current - 1].querySelector('[data-pi-next]');
    if (next) next.click();
  });
  // Clarity: lead form started (once) + email hash priming, same as the old inline handlers.
  form.addEventListener('focusin', function () {
    if (typeof win.clarity === 'function') { try { win.clarity('event', 'leadFormStarted'); } catch (e) {} }
    if (win.turboClarityCapture) win.turboClarityCapture();
  }, {once: true});
  var emailHashCache = {v: '', p: null};
  var emailEl = field('email');
  if (emailEl) emailEl.addEventListener('input', function () {
    var v = (emailEl.value || '').trim();
    if (!v || v === emailHashCache.v || !win.turboHashEmail) return;
    emailHashCache.v = v;
    emailHashCache.p = win.turboHashEmail(v);
  });

  function setMsg(text, isErr) {
    if (!msg) return;
    msg.textContent = text;
    msg.hidden = false;
    msg.className = 'pis__msg' + (isErr ? ' is-error' : '');
  }
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (msg) msg.hidden = true;
    if (current !== 3) { show(current); return; }
    var s = state();
    var res = validateStep3(s);
    if (!res.ok) { showError(res); return; }
    var token = (win.turnstile && win.turnstile.getResponse && ts) ? win.turnstile.getResponse(ts) : '';
    if (!token) { setMsg(COPY.errTurnstile, true); return; }
    if (submitBtn) submitBtn.disabled = true;
    var eid, body;
    try {
      if (submitBtn) submitBtn.textContent = COPY.sending;
      eid = (win.crypto && win.crypto.randomUUID) ? win.crypto.randomUUID() : 'lead-' + Date.now() + '-' + Math.random().toString(36).slice(2);
      body = buildBody(s, {
        token: token,
        nonce: 'PI-' + ((win.crypto && win.crypto.randomUUID) ? win.crypto.randomUUID() : Date.now() + '-' + Math.random().toString(36).slice(2)),
        source: source,
        formVariant: formVariant,
        trafficSource: (win.turboChannel ? win.turboChannel() : ''),
        eventId: eid,
        attr: (win.turboAttrFields ? win.turboAttrFields() : {}),
      });
    } catch (e) {
      resetSubmit();
      setMsg(COPY.errGeneric, true);
      return;
    }
    win.fetch(ENDPOINT, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)})
      .then(function (r) { return r.json().then(function (b) { return {s: r.status, b: b}; }); })
      .then(function (r) {
        if (r.s === 200 && r.b && r.b.ok) onSuccess(r.b, body.email, eid);
        else if (r.s === 422) { resetSubmit(); setMsg(COPY.err422, true); }
        else { resetSubmit(); setMsg((r.b && r.b.error) || COPY.errGeneric, true); }
      })
      .catch(function () { resetSubmit(); setMsg(COPY.errGeneric, true); });
  });

  function onSuccess(b, email, eid) {
    setMsg((b && b.message) || COPY.success, false);
    form.hidden = true;
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = COPY.sent; }
    try { win.localStorage.setItem('turbo_lead_sent', Date.now()); } catch (e) {}
    // Close the exit-intent popup from any OTHER form; the popup's own form keeps its card open
    // to show the thank-you line (turbo_lead_sent stops it from ever opening again).
    try { if (win.turboClosePricePopup && !(form.closest && form.closest('#pi-pop'))) win.turboClosePricePopup(); } catch (e) {}
    if (typeof win.clarity === 'function') {
      try { win.clarity('set', 'lead_status', 'submitted'); } catch (e) {}
      try { win.clarity('event', 'leadFormSubmitted'); } catch (e) {}
      try {
        (emailHashCache.v === email && emailHashCache.p ? emailHashCache.p : win.turboHashEmail(email)).then(function (h) {
          if (h) { try { win.clarity('identify', h, undefined, clarityId); } catch (e2) {} }
        }).catch(function () {});
      } catch (e) {}
    }
    var allowed = win.turboMarketingAllowed ? win.turboMarketingAllowed() : false;
    if (win.gtag && allowed) {
      try {
        win.gtag('event', 'conversion', {send_to: 'AW-18120979760/iDYVCLGSlcYcELDq4MBD'});
        win.gtag('event', 'request_price', {send_to: 'G-3KZXR1WSF3', lead_type: 'prisforfragan', placement: placement, business_line: 'renovering', event_id: eid});
      } catch (e) {}
    }
    if (allowed) {
      try {
        if (win.fbq) {
          win.fbq('track', 'Lead', {content_name: 'prisforfragan', content_category: 'renovering'}, {eventID: eid});
        } else {
          var _fbp = (doc.cookie.match(/(?:^|; )_fbp=([^;]+)/) || [])[1] || '';
          var _fbc = (doc.cookie.match(/(?:^|; )_fbc=([^;]+)/) || [])[1] || '';
          win.fetch('https://www.facebook.com/tr/?id=1050135556480642&ev=Lead&dl=' + encodeURIComponent(win.location.href) + '&eid=' + encodeURIComponent(eid) + '&cd[content_name]=prisforfragan&cd[content_category]=renovering' + (_fbp ? '&fbp=' + encodeURIComponent(_fbp) : '') + (_fbc ? '&fbc=' + encodeURIComponent(_fbc) : ''), {mode: 'no-cors', keepalive: true}).catch(function () {});
        }
      } catch (e) {}
    }
  }

  show(1);
}

export function initAll(doc, win) {
  var forms = doc.querySelectorAll('form[data-pi-steps]');
  for (var i = 0; i < forms.length; i++) initForm(forms[i], win);
  return forms.length;
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  initAll(document, window);
  window.turboPriceInquiryInit = function () { return initAll(document, window); };
  // The vehicle-search modal re-opens the same form after a success; reset it to step 1.
  window.turboPriceInquiryReset = function (form) { if (form && form.__pisReset) form.__pisReset(); };
}
